(() => {
  'use strict';

  const SELECTOR = '.hiw-dna';
  const TAU = Math.PI * 2;
  const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
  const lerp = (a, b, t) => a + (b - a) * t;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Horizontal DNA Helix Canvas ── */

  class HorizontalDNA {
    constructor(canvas) {
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d');
      this.dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.w = 0;
      this.h = 0;
      this.resize();
    }

    resize() {
      const parent = this.canvas.parentElement;
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      this.w = rect.width;
      this.h = rect.height;
      this.canvas.width = this.w * this.dpr;
      this.canvas.height = this.h * this.dpr;
    }

    render(progress) {
      const { ctx, dpr, w, h } = this;
      if (!w || !h) return;

      ctx.clearRect(0, 0, w * dpr, h * dpr);
      ctx.save();
      ctx.scale(dpr, dpr);

      const cy = h / 2;
      const amp = h * 0.32;
      const turns = 3;
      const N = 220;
      const drawN = Math.floor(N * clamp(progress, 0, 1));

      const segs = [];

      for (let i = 0; i < drawN; i++) {
        const t0 = i / N;
        const t1 = (i + 1) / N;
        const x0 = t0 * w;
        const x1 = t1 * w;

        for (let s = 0; s < 2; s++) {
          const phase = s * Math.PI;
          const a0 = t0 * turns * TAU + phase;
          const a1 = t1 * turns * TAU + phase;

          segs.push({
            type: 0,
            x0, y0: cy + Math.sin(a0) * amp,
            x1, y1: cy + Math.sin(a1) * amp,
            z: (Math.cos(a0) + Math.cos(a1)) * 0.5,
            s,
            t: (t0 + t1) * 0.5
          });
        }
      }

      const rungEvery = Math.round(N / (turns * 2));
      for (let i = rungEvery; i <= drawN; i += rungEvery) {
        const t = i / N;
        const x = t * w;
        const a = t * turns * TAU;
        segs.push({
          type: 1,
          x0: x, y0: cy + Math.sin(a) * amp,
          x1: x, y1: cy - Math.sin(a) * amp,
          z: Math.cos(a) * 0.25,
          s: -1,
          t
        });
      }

      segs.sort((a, b) => a.z - b.z);

      ctx.lineCap = 'round';

      const leadT = drawN / N;
      const fadeW = 0.08;

      for (let j = 0, len = segs.length; j < len; j++) {
        const seg = segs[j];
        const zn = (seg.z + 1) * 0.5;

        const edgeDist = leadT - seg.t;
        const edgeFade = edgeDist < fadeW ? clamp(edgeDist / fadeW, 0, 1) : 1;

        if (seg.type === 1) {
          this._drawRung(ctx, seg, zn, edgeFade);
        } else {
          this._drawStrand(ctx, seg, zn, edgeFade, leadT);
        }
      }

      if (progress > 0.01 && progress < 0.99) {
        this._drawLeadGlow(ctx, progress, w, h, cy, amp, turns);
      }

      ctx.restore();
    }

    _drawRung(ctx, seg, zn, fade) {
      const lw = 1 + zn * 1.8;
      const alpha = (0.06 + zn * 0.18) * fade;
      ctx.beginPath();
      ctx.moveTo(seg.x0, seg.y0);
      ctx.lineTo(seg.x1, seg.y1);
      ctx.lineWidth = lw;
      ctx.strokeStyle = 'rgba(255,255,255,' + alpha.toFixed(3) + ')';
      ctx.stroke();
    }

    _drawStrand(ctx, seg, zn, fade, leadT) {
      const isGreen = seg.s === 1;

      const glowW = (5 + zn * 11) * fade;
      ctx.beginPath();
      ctx.moveTo(seg.x0, seg.y0);
      ctx.lineTo(seg.x1, seg.y1);
      ctx.lineWidth = glowW;
      ctx.strokeStyle = isGreen
        ? 'rgba(185,255,54,' + ((0.02 + zn * 0.06) * fade).toFixed(3) + ')'
        : 'rgba(124,58,237,' + ((0.02 + zn * 0.06) * fade).toFixed(3) + ')';
      ctx.stroke();

      const coreW = (2 + zn * 4) * fade;
      let r, g, b;
      if (isGreen) {
        r = 80 + zn * 105;
        g = 170 + zn * 85;
        b = 20 + zn * 34;
      } else {
        r = 120 + zn * 61;
        g = 40 + zn * 47;
        b = 150 + zn * 57;
      }
      const coreAlpha = (0.35 + zn * 0.65) * fade;
      ctx.beginPath();
      ctx.moveTo(seg.x0, seg.y0);
      ctx.lineTo(seg.x1, seg.y1);
      ctx.lineWidth = coreW;
      ctx.strokeStyle = 'rgba(' + (r | 0) + ',' + (g | 0) + ',' + (b | 0) + ',' + coreAlpha.toFixed(3) + ')';
      ctx.stroke();

      if (zn > 0.35 && fade > 0.4) {
        const hlAlpha = (zn - 0.35) * 1.1 * fade;
        ctx.beginPath();
        ctx.moveTo(seg.x0, seg.y0);
        ctx.lineTo(seg.x1, seg.y1);
        ctx.lineWidth = coreW * 0.35;
        ctx.strokeStyle = isGreen
          ? 'rgba(220,255,160,' + hlAlpha.toFixed(3) + ')'
          : 'rgba(210,175,255,' + hlAlpha.toFixed(3) + ')';
        ctx.stroke();
      }

      const edgeDist = leadT - seg.t;
      if (edgeDist < 0.04 && edgeDist > 0) {
        const sparkAlpha = (1 - edgeDist / 0.04) * 0.6 * (0.5 + zn * 0.5);
        ctx.beginPath();
        ctx.moveTo(seg.x0, seg.y0);
        ctx.lineTo(seg.x1, seg.y1);
        ctx.lineWidth = coreW * 0.2;
        ctx.strokeStyle = 'rgba(255,255,255,' + sparkAlpha.toFixed(3) + ')';
        ctx.stroke();
      }
    }

    _drawLeadGlow(ctx, progress, w, h, cy, amp, turns) {
      const leadX = progress * w;
      const leadAngle = progress * turns * TAU;
      const midY = cy + (Math.sin(leadAngle) - Math.sin(leadAngle + Math.PI)) * amp * 0.25;

      const radius = 50 + amp * 0.6;
      const grad = ctx.createRadialGradient(leadX, midY, 0, leadX, midY, radius);
      grad.addColorStop(0, 'rgba(185,255,54,0.18)');
      grad.addColorStop(0.4, 'rgba(124,58,237,0.08)');
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grad;
      ctx.fillRect(leadX - radius, midY - radius, radius * 2, radius * 2);
    }
  }

  /* ── Vertical DNA Helix Canvas (mobile) ── */

  class VerticalDNA {
    constructor(canvas) {
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d');
      this.dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.w = 0;
      this.h = 0;
      this.resize();
    }

    resize() {
      const parent = this.canvas.parentElement;
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      this.w = rect.width;
      this.h = rect.height;
      this.canvas.width = this.w * this.dpr;
      this.canvas.height = this.h * this.dpr;
    }

    render(progress) {
      const { ctx, dpr, w, h } = this;
      if (!w || !h) return;

      ctx.clearRect(0, 0, w * dpr, h * dpr);
      ctx.save();
      ctx.scale(dpr, dpr);

      const cx = w / 2;
      const amp = w * 0.34;
      const turns = Math.max(3, Math.round(h / 140));
      const N = 180;
      const drawN = Math.floor(N * clamp(progress, 0, 1));

      const segs = [];

      for (let i = 0; i < drawN; i++) {
        const t0 = i / N;
        const t1 = (i + 1) / N;
        const y0 = t0 * h;
        const y1 = t1 * h;

        for (let s = 0; s < 2; s++) {
          const phase = s * Math.PI;
          const a0 = t0 * turns * TAU + phase;
          const a1 = t1 * turns * TAU + phase;

          segs.push({
            type: 0,
            x0: cx + Math.sin(a0) * amp, y0,
            x1: cx + Math.sin(a1) * amp, y1,
            z: (Math.cos(a0) + Math.cos(a1)) * 0.5,
            s, t: (t0 + t1) * 0.5
          });
        }
      }

      const rungEvery = Math.round(N / (turns * 2));
      for (let i = rungEvery; i <= drawN; i += rungEvery) {
        const t = i / N;
        const y = t * h;
        const a = t * turns * TAU;
        segs.push({
          type: 1,
          x0: cx + Math.sin(a) * amp, y0: y,
          x1: cx - Math.sin(a) * amp, y1: y,
          z: Math.cos(a) * 0.25, s: -1, t
        });
      }

      segs.sort((a, b) => a.z - b.z);
      ctx.lineCap = 'round';

      const leadT = drawN / N;
      const fadeW = 0.1;

      for (let j = 0, len = segs.length; j < len; j++) {
        const seg = segs[j];
        const zn = (seg.z + 1) * 0.5;
        const edgeDist = leadT - seg.t;
        const edgeFade = edgeDist < fadeW ? clamp(edgeDist / fadeW, 0, 1) : 1;

        if (seg.type === 1) {
          const lw = 0.8 + zn * 1.2;
          const alpha = (0.05 + zn * 0.15) * edgeFade;
          ctx.beginPath();
          ctx.moveTo(seg.x0, seg.y0);
          ctx.lineTo(seg.x1, seg.y1);
          ctx.lineWidth = lw;
          ctx.strokeStyle = 'rgba(255,255,255,' + alpha.toFixed(3) + ')';
          ctx.stroke();
        } else {
          const isGreen = seg.s === 1;
          const glowW = (3 + zn * 6) * edgeFade;
          ctx.beginPath();
          ctx.moveTo(seg.x0, seg.y0);
          ctx.lineTo(seg.x1, seg.y1);
          ctx.lineWidth = glowW;
          ctx.strokeStyle = isGreen
            ? 'rgba(185,255,54,' + ((0.02 + zn * 0.05) * edgeFade).toFixed(3) + ')'
            : 'rgba(124,58,237,' + ((0.02 + zn * 0.05) * edgeFade).toFixed(3) + ')';
          ctx.stroke();

          const coreW = (1.5 + zn * 2.5) * edgeFade;
          let r, g, b;
          if (isGreen) { r = 80 + zn * 105; g = 170 + zn * 85; b = 20 + zn * 34; }
          else { r = 120 + zn * 61; g = 40 + zn * 47; b = 150 + zn * 57; }
          const coreAlpha = (0.35 + zn * 0.65) * edgeFade;
          ctx.beginPath();
          ctx.moveTo(seg.x0, seg.y0);
          ctx.lineTo(seg.x1, seg.y1);
          ctx.lineWidth = coreW;
          ctx.strokeStyle = 'rgba(' + (r | 0) + ',' + (g | 0) + ',' + (b | 0) + ',' + coreAlpha.toFixed(3) + ')';
          ctx.stroke();
        }
      }

      ctx.restore();
    }
  }

  /* ── Section Controller ── */

  class HowItWorksDNA {
    constructor(section) {
      this.section = section;
      this.scrollEl = section.querySelector('[data-hiw-scroll]');
      this.canvas = section.querySelector('[data-dna-canvas]');
      this.allStepEls = Array.from(section.querySelectorAll('[data-hiw-step]'));
      this.renderer = null;
      this.ticking = false;
      this.revealed = new Set();
      this.isMobile = false;
      this.mobileObserver = null;
      this.mobileRenderer = null;
      this._mTicking = false;
      this._onMobileScroll = null;

      if (!this.scrollEl || !this.canvas) return;

      this.renderer = new HorizontalDNA(this.canvas);
      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._tick = this._tick.bind(this);

      this.init();
    }

    init() {
      this.section.classList.add('is-ready');
      this.isMobile = window.innerWidth <= 860;

      if (prefersReducedMotion) {
        this.renderer.render(1);
        this.allStepEls.forEach(el => el.classList.add('is-visible'));
        this.section.querySelectorAll('[data-hiw-mobile-step]').forEach(el => el.classList.add('is-visible'));
        const mc = this.section.querySelector('[data-dna-mobile-canvas]');
        if (mc) new VerticalDNA(mc).render(1);
        return;
      }

      if (this.isMobile) {
        this._initMobile();
      } else {
        this._initDesktop();
      }
    }

    _initDesktop() {
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });
      this._tick();
    }

    _initMobile() {
      const mobileCanvas = this.section.querySelector('[data-dna-mobile-canvas]');
      if (mobileCanvas) {
        this.mobileRenderer = new VerticalDNA(mobileCanvas);
      }

      this._onMobileScroll = () => {
        if (!this._mTicking) {
          this._mTicking = true;
          requestAnimationFrame(() => {
            this._mTicking = false;
            this._updateMobileHelix();
          });
        }
      };

      if (this.mobileRenderer) {
        window.addEventListener('scroll', this._onMobileScroll, { passive: true });
        window.addEventListener('resize', this._onResize, { passive: true });
        this._updateMobileHelix();
      }

      const mobileCards = Array.from(this.section.querySelectorAll('[data-hiw-mobile-step]'));

      this.mobileObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
            }
          });
        },
        { root: null, threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
      );

      mobileCards.forEach(el => this.mobileObserver.observe(el));
    }

    _updateMobileHelix() {
      if (!this.mobileRenderer) return;
      const flow = this.section.querySelector('[data-hiw-mobile-flow]');
      if (!flow) return;
      const rect = flow.getBoundingClientRect();
      const vh = window.innerHeight;
      const total = rect.height + vh;
      const traveled = vh - rect.top;
      const progress = clamp(traveled / total, 0, 1);
      this.mobileRenderer.render(progress);
    }

    _onScroll() {
      if (!this.ticking) {
        this.ticking = true;
        requestAnimationFrame(this._tick);
      }
    }

    _onResize() {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 860;

      if (wasMobile !== this.isMobile) {
        this.destroy();
        this.revealed.clear();
        this.allStepEls.forEach(el => el.classList.remove('is-visible'));
        this.section.querySelectorAll('[data-hiw-mobile-step]').forEach(el => el.classList.remove('is-visible'));
        this.init();
        return;
      }

      if (this.renderer) this.renderer.resize();
      if (this.mobileRenderer) this.mobileRenderer.resize();
      if (!this.isMobile) this._tick();
      if (this.isMobile) this._updateMobileHelix();
    }

    _tick() {
      this.ticking = false;

      const rect = this.scrollEl.getBoundingClientRect();
      if (rect.bottom < -100 || rect.top > window.innerHeight + 100) return;

      const scrollable = Math.max(rect.height - window.innerHeight, 1);
      const traveled = clamp(-rect.top, 0, scrollable);
      const progress = clamp(traveled / scrollable, 0, 1);

      this.renderer.render(progress);

      const thresholds = [0.0, 0.25, 0.50, 0.75];
      for (let i = 0; i < thresholds.length; i++) {
        const shouldShow = progress >= thresholds[i];
        const isShown = this.revealed.has(i);

        if (shouldShow && !isShown) {
          this.revealed.add(i);
          this._setStepVisible(i, true);
        } else if (!shouldShow && isShown) {
          this.revealed.delete(i);
          this._setStepVisible(i, false);
        }
      }

      this.section.style.setProperty('--hiw-progress', progress.toFixed(4));
    }

    _setStepVisible(index, visible) {
      const els = this.section.querySelectorAll('[data-hiw-step="' + index + '"]');
      els.forEach(el => {
        if (visible) {
          el.classList.add('is-visible');
        } else {
          el.classList.remove('is-visible');
        }
      });
    }

    destroy() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      if (this._onMobileScroll) {
        window.removeEventListener('scroll', this._onMobileScroll);
        this._onMobileScroll = null;
      }
      if (this.mobileObserver) {
        this.mobileObserver.disconnect();
        this.mobileObserver = null;
      }
      this.mobileRenderer = null;
    }
  }

  /* ── Bootstrap ── */

  const initAll = () => {
    document.querySelectorAll(SELECTOR).forEach((section) => {
      if (section.__hiwDNA) return;
      section.__hiwDNA = new HowItWorksDNA(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const ct = e.target;
    const sec = ct.matches(SELECTOR) ? ct : ct.querySelector(SELECTOR);
    if (!sec || sec.__hiwDNA) return;
    sec.__hiwDNA = new HowItWorksDNA(sec);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const ct = e.target;
    const sec = ct.matches(SELECTOR) ? ct : ct.querySelector(SELECTOR);
    if (!sec || !sec.__hiwDNA) return;
    sec.__hiwDNA.destroy();
    delete sec.__hiwDNA;
  });
})();
